import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatEatTime } from '../../utils/datetime';
import { Card, Spinner } from '../shared/UI';
import { DailyTrendChart } from './DailyTrendChart';

function StatCard({ label, value, icon, color, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 'var(--radius-lg)', padding: '24px 28px',
      border: '1px solid var(--gray-100)', boxShadow: 'var(--shadow-sm)',
      display: 'flex', flexDirection: 'column', gap: 8, animation: 'fadeIn 0.4s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)', fontFamily: 'var(--font-display)' }}>{label}</span>
        <span style={{ fontSize: 22, background: color + '18', padding: '7px 10px', borderRadius: 10 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 42, fontWeight: 800, fontFamily: 'var(--font-display)', color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
}

function fmtDuration(secs) {
  if (secs === null || secs === undefined || secs === 0) return '—';
  const m = Math.floor(secs / 60), s = Math.round(secs % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export function StatsDashboard() {
  const [stats, setStats] = useState(null);
  const [counters, setCounters] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [counterStats, setCounterStats] = useState([]);
  const load = async () => {
    const [s, c, t, cs] = await Promise.all([
      api.get('/api/tickets/stats'),
      api.get('/api/counters/?active=true'),
      api.get('/api/tickets/?').catch(() => []),
      api.get('/api/admin/counter-stats').catch(() => []),
    ]);
    setStats(s);
    setCounters(c);
    setRecentTickets(Array.isArray(t) ? t.slice(0, 8) : []);
    setCounterStats(Array.isArray(cs) ? cs : []);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  if (!stats) return <Spinner />;

  const statusColor = { Waiting: '#f59e0b', Open: '#10b981', Closed: '#3b82f6', Skipped: '#ef4444' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)', letterSpacing: '-0.02em' }}>Dashboard</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Live queue overview — updates in real time</p>
      </div>

      {/* Stats grid */}
      <div className="resp-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        <StatCard label="Total Today" value={stats.total} icon="🎫" color="var(--cobalt)" />
        <StatCard label="Waiting" value={stats.waiting} icon="⏳" color="#f59e0b" sub="In queue now" />
        <StatCard label="Active (Open)" value={stats.active} icon="▶" color="#10b981" sub="Currently being served" />
        <StatCard label="Closed" value={stats.closed} icon="✓" color="#3b82f6" sub="Completed today" />
        <StatCard label="Skipped" value={stats.skipped} icon="⏭" color="#ef4444" sub="Missed / no-show" />
        <StatCard label="Counters Live" value={counters.length} icon="⊞" color="var(--navy)" sub="Active counters" />
      </div>

      {/* Daily Trend Chart */}
      <DailyTrendChart />

      {/* Recent tickets table */}
      <Card>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--gray-100)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: 'var(--navy)' }}>Recent Activity</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                {['Ticket', 'Service', 'Counter', 'Customer', 'Status', 'Time'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-500)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentTickets.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>No tickets yet today</td></tr>
              )}
              {recentTickets.map((t, i) => (
                <tr key={t.id} style={{ borderTop: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', animation: 'fadeIn 0.3s ease' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13, color: 'var(--cobalt)' }}>{t.ticket_number}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--navy)' }}>{t.service_name || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-700)' }}>{t.counter_name || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-700)' }}>{t.customer_name || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-display)', textTransform: 'uppercase', background: statusColor[t.status] + '18', color: statusColor[t.status] }}>
                      {t.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)', fontFamily: 'var(--font-mono)' }}>
                    {formatEatTime(t.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Counter Statistics table */}
      <Card>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--gray-100)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: 'var(--navy)' }}>Counter Performance Statistics</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                {['Counter Label', 'Counter Name', 'Clients Served', 'Avg Service Time', 'Max Service Time', 'Min Service Time'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-500)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {counterStats.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>No counter statistics available</td></tr>
              )}
              {counterStats.map((c, i) => (
                <tr key={c.counter_id} style={{ borderTop: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', animation: 'fadeIn 0.3s ease' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13, color: 'var(--cobalt)' }}>{c.counter_label}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--navy)' }}>{c.counter_name}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-700)', fontWeight: 600 }}>{c.served_count}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--navy)', fontFamily: 'var(--font-mono)' }}>{fmtDuration(c.avg_duration)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--red-dark)', fontFamily: 'var(--font-mono)' }}>{fmtDuration(c.max_duration)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--green-dark)', fontFamily: 'var(--font-mono)' }}>{fmtDuration(c.min_duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
